/**
 * Pipeline — the board page: filters, columns, drawer, stage admin.
 * Filters live in the URL (`q`, `role_id`, `shopId`, `lead`).
 */
import { useEffect, useState } from 'react';
import { Inbox } from 'lucide-react';
import { hasMinRole, useAuth } from '@/lib/auth';
import { usePageTitle } from '@/lib/page-title';
import { useDebouncedValue } from '@/lib/use-debounce';
import { errorMessage, useToast } from '@/components/Toast';
import { LeadDrawer } from '@/features/leads/LeadDrawer';
import { StageDeleteModal } from '@/features/stages/StageDeleteModal';
import { useStageAdmin } from '@/features/stages/use-stage-admin';
import { useLeads, useStages } from './api';
import { Board } from './Board';
import { FiltersBar } from './FiltersBar';
import { useBoardFilters } from './use-filters';
import { useMoveLead } from './use-move-lead';

function BoardSkeleton() {
  return (
    <div className="flex h-full gap-3 overflow-hidden px-4 sm:px-6" aria-busy="true" aria-label="Loading board">
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className="h-full w-[272px] shrink-0 animate-pulse rounded-lg bg-gray-200/50 p-2">
          <div className="h-5 w-32 rounded bg-gray-300/60" />
          <div className="mt-3 space-y-2">
            <div className="h-16 rounded-lg bg-white/80" />
            <div className="h-16 rounded-lg bg-white/60" />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="mx-4 flex flex-col items-center rounded-xl border border-dashed border-gray-300 bg-white/70 px-6 py-10 text-center sm:mx-6">
      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-navy text-white">
        <Inbox size={20} strokeWidth={2} aria-hidden />
      </div>
      <h2 className="mt-3 text-base font-semibold text-gray-900">No candidates yet</h2>
      <p className="mt-1 max-w-sm text-sm text-muted">
        Connect a landing page to <code className="mono text-[12px] text-gray-800">POST /api/intake/webform</code> and
        applicants will appear here in the first stage.
      </p>
    </div>
  );
}

export default function PipelinePage() {
  const { user } = useAuth();
  const isAdmin = hasMinRole(user, 'company_admin');
  const toast = useToast();
  usePageTitle('Pipeline');
  const { filters, update, setShop, clear, hasActive } = useBoardFilters();

  const [search, setSearch] = useState(filters.q);
  const debounced = useDebouncedValue(search, 300);
  useEffect(() => {
    if (debounced !== filters.q) update({ q: debounced });
  }, [debounced]); // eslint-disable-line react-hooks/exhaustive-deps

  const stagesQ = useStages();
  const leadsQ = useLeads({ q: filters.q, role_id: filters.roleId, shop_id: filters.shopId });
  const stages = stagesQ.data ?? [];
  const leads = leadsQ.data ?? [];
  const admin = useStageAdmin(stages);
  const move = useMoveLead();

  const onMove = (leadId: string, toStageId: string) =>
    move.mutate(
      { leadId, toStageId },
      { onError: (err) => toast.push({ kind: 'error', title: 'Move failed', message: errorMessage(err) }) },
    );

  const error = stagesQ.error ?? leadsQ.error;
  const loading = stagesQ.isPending || leadsQ.isPending;
  const showEmpty = !loading && !error && leads.length === 0 && !hasActive;

  return (
    <div className="flex h-full flex-col">
      {/* Same title scale and top offset as PageHeader on the settings pages (Stage 7 design pass). */}
      <div className="px-4 pb-3 pt-5 sm:px-6 sm:pt-6">
        <div className="mb-3 flex items-baseline gap-3">
          <h1 className="text-xl font-semibold tracking-tight text-gray-900 sm:text-2xl">Pipeline</h1>
        </div>
        <FiltersBar
          search={search}
          onSearch={setSearch}
          roleId={filters.roleId}
          onRole={(v) => update({ roleId: v })}
          shopId={filters.shopId}
          onShop={setShop}
          onClear={() => {
            setSearch('');
            clear();
          }}
          hasActive={hasActive}
          total={leads.length}
          loading={loading}
        />
      </div>

      <div className="min-h-0 flex-1">
        {loading ? (
          <BoardSkeleton />
        ) : error ? (
          <div className="mx-4 rounded-lg border border-danger/30 bg-danger-bg px-4 py-3 text-sm text-danger sm:mx-6" role="alert">
            Could not load the board: {errorMessage(error)}{' '}
            <button type="button" className="font-medium underline" onClick={() => { void stagesQ.refetch(); void leadsQ.refetch(); }}>
              Retry
            </button>
          </div>
        ) : showEmpty ? (
          <EmptyState />
        ) : (
          <Board stages={stages} leads={leads} admin={isAdmin ? admin : null} onOpenLead={(id) => update({ leadId: id }, { replace: false })} onMove={onMove} />
        )}
      </div>

      <LeadDrawer leadId={filters.leadId} onClose={() => update({ leadId: null })} />
      {isAdmin && (
        <StageDeleteModal
          stage={admin.pendingDelete?.stage ?? null}
          stages={stages}
          count={admin.pendingDelete?.count ?? 0}
          busy={admin.deleting}
          onConfirm={admin.confirmDelete}
          onClose={admin.cancelDelete}
        />
      )}
    </div>
  );
}
